import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Shop management store using Zustand
 * Centralizes shop-related state like shop data, products, and settings
 */
export const useShopStore = create(
  devtools(
    persist(
      (set, get) => ({
        // Shop data
        shop: null,
        products: [],
        shopSettings: null,
        
        // UI state
        editMode: false,
        isEditing: false,
        showDeleteConfirm: false,
        productToDelete: null,
        
        // Loading states
        loading: {
          shop: false,
          products: false,
          settings: false,
          save: false,
        },
        
        // Error states
        errors: {
          shop: null,
          products: null,
          settings: null,
          save: null,
        },

        // Actions
        setShop: (shop) => set({ shop }),
        
        setProducts: (products) => set({ products }),
        
        addProduct: (product) =>
          set((state) => ({
            products: [...state.products, product],
          })),
        
        updateProduct: (productId, updates) =>
          set((state) => ({
            products: state.products.map((p) =>
              p.id === productId ? { ...p, ...updates } : p
            ),
          })),
        
        removeProduct: (productId) =>
          set((state) => ({
            products: state.products.filter((p) => p.id !== productId),
          })),
        
        reorderProducts: (newOrder) => set({ products: newOrder }),
        
        setShopSettings: (settings) => set({ shopSettings: settings }),
        
        setEditMode: (editMode) => set({ editMode }),
        
        setIsEditing: (isEditing) => set({ isEditing }),
        
        setShowDeleteConfirm: (show) => set({ showDeleteConfirm: show }),
        
        setProductToDelete: (product) => set({ productToDelete: product }),
        
        setLoading: (key, value) =>
          set((state) => ({
            loading: {
              ...state.loading,
              [key]: value,
            },
          })),
        
        setError: (key, error) =>
          set((state) => ({
            errors: {
              ...state.errors,
              [key]: error,
            },
          })),
        
        clearError: (key) =>
          set((state) => ({
            errors: {
              ...state.errors,
              [key]: null,
            },
          })),
        
        clearAllErrors: () =>
          set((state) => ({
            errors: Object.keys(state.errors).reduce(
              (acc, key) => ({ ...acc, [key]: null }),
              {}
            ),
          })),

        // Utility functions
        isLoading: (key) => get().loading[key] || false,
        hasError: (key) => !!get().errors[key],
        getError: (key) => get().errors[key],
        getProduct: (id) => get().products.find((p) => p.id === id),
        getProductsByCategory: (category) =>
          get().products.filter((p) => p.category === category),
        getProductsByStatus: (status) =>
          get().products.filter((p) => p.status === status),
        getAvailableProducts: () =>
          get().products.filter((p) => p.available),
        getProductCount: () => get().products.length,
        getShopId: () => get().shop?.id,
        getShopName: () => get().shop?.name,
        isShopOwner: (userId) => get().shop?.sellerId === userId,

        // Reset store
        reset: () =>
          set({
            shop: null,
            products: [],
            shopSettings: null,
            editMode: false,
            isEditing: false,
            showDeleteConfirm: false,
            productToDelete: null,
            loading: {
              shop: false,
              products: false,
              settings: false,
              save: false,
            },
            errors: {
              shop: null,
              products: null,
              settings: null,
              save: null,
            },
          }),
      }),
      {
        name: 'shop-store',
        partialize: (state) => ({
          shop: state.shop,
          shopSettings: state.shopSettings,
          editMode: state.editMode,
        }),
      }
    ),
    {
      name: 'shop-store',
    }
  )
);
